/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import DocumentViewerModal from "components/DocumentViewer";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteEvidence,
  fetchEvidenceByIncidentRegistrationNumber,
} from "services/incidentService";
import { CrimeReference, Evidence, FILE_URL, Investigation } from "types";
import { deleteIcon, editIcon, viewIcon } from "utils/constant";
import { errorHandler } from "utils/helper";
import EvidenceDetail from "./modals/EvidenceDetail";
import EvidenceForm from "./modals/EvidenceForm";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident: CrimeReference;
  canManageIncidentDetail: boolean;
};

const EvidenceFilesTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [evidences, setEvidences] = useState<Evidence[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [loading, setLoading] = React.useState(true);
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedEvidence, setSelectedEvidence] = React.useState<Evidence>();

  const fetchEvidenceData = async () => {
    setLoading(true);
    fetchEvidenceByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then((data) => {
        setEvidences(data);
        setLoading(false);
      })
      .catch((error) =>
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        ),
      );
  };

  useEffect(() => {
    fetchEvidenceData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">📄</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("evidence_files")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_evidence_files_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <ActionButton
              onClick={() => {
                setIsOpenForm(true);
                setAction("Add");
              }}
            >
              ➕ {t("add_evidence")}
            </ActionButton>
          )}
        </div>
      </div>

      {evidences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_evidence_files_found")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {evidences.map((evidence, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600">📄</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {evidence?.evidence_type}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("collected_by")}: {evidence?.collected_by || t("na")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEvidence(evidence);
                      setIsOpenDetail(true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setAction("Edit");
                            setSelectedEvidence(evidence);
                            setIsOpenForm(true);
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_evidence")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEvidence(evidence);
                            setIsOpenDelete(true);
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_evidence")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>

              <div className="mt-4">
                <DocumentViewerModal
                  documents={(evidence?.documents || []) as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.EVIDENCE}`}
                  layout="list"
                  hideEmptyFile={true}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {isOpenForm && (
        <EvidenceForm
          action={action}
          investigation={investigation}
          incident={incident}
          evidence={action === "Edit" ? selectedEvidence : undefined}
          isOpen={isOpenForm}
          onClose={(refetch) => {
            setIsOpenForm(false);
            if (refetch) fetchEvidenceData();
          }}
        />
      )}
      {isOpenDetail && (
        <EvidenceDetail
          evidence={selectedEvidence}
          isOpen={isOpenDetail}
          onClose={() => setIsOpenDetail(false)}
        />
      )}
      {selectedEvidence && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteEvidence(selectedEvidence)
              .then(() => {
                showTooltip(t("evidence_deleted_successfully"), "success");
                fetchEvidenceData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail ||
                    errorHandler(error) ||
                    t("something_went_wrong_try_again"),
                  "error",
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title={t("delete_evidence")}
          message={t("confirm_delete_evidence")}
        />
      )}
    </div>
  );
};

export default EvidenceFilesTab;
